import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import { colors } from "../../dark-hero/colors";
import { PageContainer } from "../../dark-hero/PageContainer";
import {
  DATA_CONTROLLER,
  GOVERNING_COUNTRY,
  formatControllerAddress,
  formatControllerRegistration,
  isControllerDetailUnfilled,
} from "../legalController";
import { SUPPORT_EMAIL } from "../publicFacts";
import {
  DISCORD_INVITE_URL,
} from "../../../public-site/siteData";
import {
  DARK_HERO_HEADER_HEIGHT_PX,
  DARK_HERO_HEADER_XL_HEIGHT_PX,
} from "../../publicHeaderLayout";

const PAGE_BG = "#080808";
const LAST_UPDATED = "16 October 2026";
const CONTROLLER_REGISTRATION = formatControllerRegistration();
const CONTROLLER_ADDRESS = formatControllerAddress();

const TOC_ITEMS = [
  { id: "g1",  num: "1.",  label: "Data controller" },
  { id: "g2",  num: "2.",  label: "Data we collect" },
  { id: "g3",  num: "3.",  label: "Legal basis" },
  { id: "g4",  num: "4.",  label: "Your rights" },
  { id: "g5",  num: "5.",  label: "Data retention" },
  { id: "g6",  num: "6.",  label: "Data security" },
  { id: "g7",  num: "7.",  label: "International transfers" },
  { id: "g8",  num: "8.",  label: "Automated decisions" },
  { id: "g9",  num: "9.",  label: "Complaints" },
  { id: "g10", num: "10.", label: "Policy changes" },
  { id: "g11", num: "11.", label: "Contact us" },
];

const sectionLabelSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: colors.text.muted,
  mb: 1,
};

const sectionTitleSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "22px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: colors.text.primary,
  mb: 2.75,
  lineHeight: 1.1,
};

const bodySx = {
  fontSize: "15px",
  color: colors.text.secondary,
  lineHeight: 1.85,
  mb: 2,
  "& strong": { color: colors.text.primary, fontWeight: 600 },
  "& a": {
    color: colors.primary.main,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
};

const sidebarLinkSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
  gap: 1,
  p: "10px 14px",
  textDecoration: "none",
  border: `1px solid ${colors.border.subtle}`,
  color: colors.text.muted,
  transition: "color 0.2s, border-color 0.2s",
  "&:hover": { color: colors.text.secondary, borderColor: colors.border.default },
};

function DocList({ items, keyWidth = 150 }) {
  return (
    <Box sx={{ my: 2.5 }}>
      {items.map((item) => (
        <Box
          key={item.key}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: `${keyWidth}px 1fr` },
            gap: { xs: 0.5, sm: 2.5 },
            py: 2,
            borderBottom: `1px solid ${colors.border.subtle}`,
            "&:first-of-type": { borderTop: `1px solid ${colors.border.subtle}` },
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: colors.text.primary,
              pt: { sm: "1px" },
            }}
          >
            {item.key}
          </Typography>
          <Typography component="div" sx={{ ...bodySx, mb: 0 }}>
            {item.val}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function Callout({ children }) {
  return (
    <Box
      sx={{
        my: 3,
        p: "18px 22px",
        background: "linear-gradient(180deg, rgba(232,163,61,0.04), transparent)",
        border: "1px solid rgba(232,163,61,0.25)",
        borderLeft: `2px solid ${colors.primary.main}`,
        "& p": { fontSize: "13px", color: colors.text.secondary, lineHeight: 1.7, m: 0 },
        "& strong": { color: colors.primary.main, fontWeight: 600 },
      }}
    >
      {children}
    </Box>
  );
}

function RightsGrid() {
  const rights = [
    {
      title: "Access",
      body: "Request a copy of the personal data we hold about you at any time.",
    },
    {
      title: "Rectification",
      body: "Ask us to correct or update any inaccurate or incomplete personal data.",
    },
    {
      title: "Erasure",
      body: "Request deletion of your data, subject to certain legal obligations we may hold.",
    },
    {
      title: "Restrict processing",
      body: "Limit how we process your data under certain circumstances defined by the GDPR.",
    },
    {
      title: "Data portability",
      body: "Receive your data in a structured, machine-readable format and transfer it elsewhere.",
    },
    {
      title: "Object",
      body: "Object to processing for specific purposes, including direct marketing or legitimate-interest grounds.",
    },
    {
      title: "Withdraw consent",
      body: "Where we rely on consent, you may withdraw it at any time without affecting prior processing.",
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
        gap: "2px",
        my: 2.5,
      }}
    >
      {rights.map((r) => (
        <Box
          key={r.title}
          sx={{
            position: "relative",
            overflow: "hidden",
            background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
            border: `1px solid ${colors.border.subtle}`,
            p: "18px 20px",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "2px",
              bgcolor: colors.primary.main,
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: colors.primary.main,
              mb: 0.75,
            }}
          >
            {r.title}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: colors.text.secondary, lineHeight: 1.65 }}>
            {r.body}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function DocSection({ id, num, title, children }) {
  return (
    <Box
      component="section"
      id={id}
      sx={{
        pt: "52px",
        borderTop: `1px solid ${colors.border.subtle}`,
        scrollMarginTop: {
          xs: `${DARK_HERO_HEADER_HEIGHT_PX + 16}px`,
          xl: `${DARK_HERO_HEADER_XL_HEIGHT_PX + 16}px`,
        },
      }}
    >
      <Typography sx={sectionLabelSx}>Section {num}</Typography>
      <Typography component="h2" sx={sectionTitleSx}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function DiscordIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

const contactIconSx = {
  width: 32,
  height: 32,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: colors.background.surface,
  border: `1px solid ${colors.border.subtle}`,
  color: colors.primary.main,
};

function ControllerContact({ email }) {
  const isEmail = typeof email === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  if (!isEmail) {
    return <strong>{email}</strong>;
  }
  return (
    <Box component="a" href={`mailto:${email}`} sx={{ color: colors.primary.main, textDecoration: "none" }}>
      {email}
    </Box>
  );
}

export default function GDPRPage() {
  const [activeSection, setActiveSection] = React.useState("g1");

  React.useEffect(() => {
    const sections = TOC_ITEMS.map((item) => document.getElementById(item.id)).filter(Boolean);
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -75% 0px" }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleTocClick = (event, id) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(id);
  };

  return (
    <Box sx={{ backgroundColor: PAGE_BG, color: colors.text.primary, overflowX: "hidden" }}>
      <PageContainer sx={{ maxWidth: "1140px", px: { xs: 0, md: 0 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            minHeight: "100vh",
          }}
        >
          {/* ── Sidebar ── */}
          <Box
            component="aside"
            sx={{
              width: { xs: "100%", md: "240px" },
              flexShrink: 0,
              position: { md: "sticky" },
              top: { md: `${DARK_HERO_HEADER_HEIGHT_PX}px`, xl: `${DARK_HERO_HEADER_XL_HEIGHT_PX}px` },
              alignSelf: "flex-start",
              maxHeight: {
                md: `calc(100vh - ${DARK_HERO_HEADER_HEIGHT_PX}px)`,
                xl: `calc(100vh - ${DARK_HERO_HEADER_XL_HEIGHT_PX}px)`,
              },
              overflowY: { md: "auto" },
              py: { xs: 3, md: 7 },
              px: { xs: 3, md: "40px 40px 56px 48px" },
              borderRight: { md: `1px solid ${colors.border.subtle}` },
              borderBottom: { xs: `1px solid ${colors.border.subtle}`, md: "none" },
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: "9px",
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: colors.text.muted,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&::before": {
                  content: '""',
                  width: 12,
                  height: "1px",
                  bgcolor: colors.text.muted,
                  display: "block",
                },
              }}
            >
              Contents
            </Typography>

            <Box component="nav" sx={{ display: "flex", flexDirection: "column" }}>
              {TOC_ITEMS.map((item) => {
                const active = activeSection === item.id;
                return (
                  <Box
                    key={item.id}
                    component="a"
                    href={`#${item.id}`}
                    onClick={(event) => handleTocClick(event, item.id)}
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1.25,
                      py: 1.125,
                      borderBottom: "1px solid rgba(255,255,255,0.03)",
                      textDecoration: "none",
                      fontSize: "12px",
                      lineHeight: 1.4,
                      color: active ? colors.primary.main : colors.text.muted,
                      transition: "color 0.15s",
                      "&:hover": { color: active ? colors.primary.main : colors.text.secondary },
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: "10px",
                        fontWeight: 600,
                        letterSpacing: "0.1em",
                        flexShrink: 0,
                        width: 20,
                        color: active ? colors.primary.main : colors.text.muted,
                      }}
                    >
                      {item.num}
                    </Box>
                    {item.label}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ height: "1px", bgcolor: colors.border.subtle, my: 2.5 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {[
                { label: "Last updated", val: LAST_UPDATED },
                { label: "Governing law", val: `${GOVERNING_COUNTRY} / EU` },
                { label: "Regulation", val: "GDPR (EU) 2016/679" },
              ].map((row) => (
                <Box key={row.label}>
                  <Typography
                    sx={{
                      fontFamily: "'Inter', system-ui, sans-serif",
                      fontSize: "9px",
                      fontWeight: 700,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: colors.text.muted,
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: colors.text.secondary }}>{row.val}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1 }}>
              <Box component={Link} to="/terms-of-use" sx={sidebarLinkSx}>
                Terms of Use →
              </Box>
              <Box component={Link} to="/privacy" sx={sidebarLinkSx}>
                Privacy Policy →
              </Box>
            </Box>
          </Box>

          {/* ── Main content ── */}
          <Box
            component="article"
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: "720px",
              py: { xs: 5, md: 7 },
              px: { xs: 3, md: "64px 48px 120px" },
              pb: { xs: 10, md: 15 },
            }}
          >
            {/* Header */}
            <Box component="header" sx={{ mb: 7, pb: 5, borderBottom: `1px solid ${colors.border.subtle}` }}>
              <Typography
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: colors.primary.main,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  mb: 2.5,
                  "&::before": {
                    content: '""',
                    width: 20,
                    height: "1px",
                    bgcolor: colors.primary.main,
                    display: "block",
                  },
                }}
              >
                Legal document
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "4.25rem" },
                  fontWeight: 700,
                  textTransform: "uppercase",
                  lineHeight: 0.92,
                  letterSpacing: "-0.01em",
                  color: colors.text.primary,
                  mb: 3,
                }}
              >
                Data
                <br />
                &amp; GDPR
              </Typography>
              <Typography
                sx={{ fontSize: "16px", fontWeight: 300, color: colors.text.secondary, lineHeight: 1.8, mb: 3.5 }}
              >
                Ouro is committed to protecting your personal data and complying with the General Data
                Protection Regulation (GDPR). This page explains how we collect, use, and protect your data,
                and what rights you hold as a user.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 3,
                  pt: 2.5,
                  borderTop: `1px solid ${colors.border.subtle}`,
                }}
              >
                {[
                  { label: "Last updated", val: LAST_UPDATED },
                  { label: "Applies to", val: "ouro.is and all Ouro services" },
                  {
                    label: "Questions",
                    val: (
                      <Box
                        component="a"
                        href={`mailto:${SUPPORT_EMAIL}`}
                        sx={{ color: colors.primary.main, textDecoration: "none" }}
                      >
                        {SUPPORT_EMAIL}
                      </Box>
                    ),
                  },
                ].map((item) => (
                  <Box key={item.label}>
                    <Typography
                      sx={{
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: "9px",
                        fontWeight: 700,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: colors.text.muted,
                        mb: 0.375,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: colors.text.secondary }}>{item.val}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Section 1 */}
            <DocSection id="g1" num="01" title="Data controller">
              <Typography sx={bodySx}>
                Ouro is a marketing name of{" "}
                <strong>
                  {isControllerDetailUnfilled(DATA_CONTROLLER.name)
                    ? `a company registered in ${DATA_CONTROLLER.country}`
                    : DATA_CONTROLLER.name}
                </strong>
                {CONTROLLER_REGISTRATION ? <> ({CONTROLLER_REGISTRATION})</> : null}
                {CONTROLLER_ADDRESS ? <>, headquartered at {CONTROLLER_ADDRESS}</> : null}
                . This entity is the data controller responsible for your personal
                data under the GDPR.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                For any questions or concerns regarding how your data is handled, please contact us at{" "}
                <ControllerContact email={DATA_CONTROLLER.contactEmail} />.
              </Typography>
            </DocSection>

            {/* Section 2 */}
            <DocSection id="g2" num="02" title="Personal data we collect">
              <Typography sx={bodySx}>
                Ouro collects and processes the following categories of personal data in order to provide and
                improve the platform.
              </Typography>
              <DocList
                keyWidth={180}
                items={[
                  {
                    key: "Contact information",
                    val: "To create an account you sign in with Steam. We receive your public Steam profile. A Steam account is all you need to join.",
                  },
                  {
                    key: "Identification",
                    val: "ID verification is optional and is not required to sign up or play. If you choose it, the check is done through didit.me and it adds to your reputation.",
                  },
                  {
                    key: "Gameplay data",
                    val: "Match history, in-game statistics, and behavioral patterns generated through your use of the matchmaking service.",
                  },
                  {
                    key: "Technical data",
                    val: "IP address, device type, browser type, and other technical information required for the secure operation of the service.",
                  },
                  {
                    key: "Communication data",
                    val: "Information from your interactions with Ouro, including support inquiries, feedback submissions, and moderation-related communications.",
                  },
                ]}
              />
            </DocSection>

            {/* Section 3 */}
            <DocSection id="g3" num="03" title="Legal basis for processing">
              <Typography sx={bodySx}>
                We only process your personal data where we have a valid legal basis to do so. The grounds we
                rely on are set out below.
              </Typography>
              <DocList
                keyWidth={200}
                items={[
                  {
                    key: "Performance of a contract",
                    val: "We process your data to provide and maintain the matchmaking service as described in our Terms of Use.",
                  },
                  {
                    key: "Consent",
                    val: "Optional ID verification through didit.me is based on your consent. You can skip it and still join with Steam. You may withdraw that consent at any time.",
                  },
                  {
                    key: "Legitimate interests",
                    val: "We process data to improve our services, ensure platform security, prevent fraud, and protect the interests of our users and the platform.",
                  },
                  {
                    key: "Legal obligations",
                    val: "We may process your data where required to comply with applicable law or respond to lawful requests from authorities.",
                  },
                ]}
              />
            </DocSection>

            {/* Section 4 */}
            <DocSection id="g4" num="04" title="Your rights under the GDPR">
              <Typography sx={bodySx}>
                As a user in the European Union, the GDPR grants you the following rights over your personal
                data. You may exercise any of these rights by contacting us at{" "}
                <Box component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: colors.primary.main, textDecoration: "none" }}>
                  {SUPPORT_EMAIL}
                </Box>
                .
              </Typography>
              <RightsGrid />
              <Callout>
                <Typography component="p">
                  If you are signed in to Ouro, you can also permanently delete your account directly
                  from <strong>Account Settings → Danger Zone</strong> without contacting us.
                </Typography>
              </Callout>
            </DocSection>

            {/* Section 5 */}
            <DocSection id="g5" num="05" title="Data retention">
              <Typography sx={bodySx}>
                We retain your personal data only for as long as necessary to fulfil the purposes for which it
                was collected, including any legal, accounting, or reporting obligations.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                Once data is no longer required, it is securely deleted or anonymised. Where we must retain
                limited records for legal, fraud-prevention, billing, moderation, or competitive-integrity
                reasons, those records are kept only in a reduced or anonymised form.
              </Typography>
            </DocSection>

            {/* Section 6 */}
            <DocSection id="g6" num="06" title="Data security">
              <Typography sx={bodySx}>
                We implement appropriate technical and organisational measures to protect your personal data
                against unauthorised access, alteration, disclosure, or destruction.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                While we apply commercially reasonable protections, no method of transmission over the internet
                or electronic storage can be guaranteed 100% secure. If you suspect any unauthorised access to
                your account, please notify us immediately at{" "}
                <Box component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: colors.primary.main, textDecoration: "none" }}>
                  {SUPPORT_EMAIL}
                </Box>
                .
              </Typography>
            </DocSection>

            {/* Section 7 */}
            <DocSection id="g7" num="07" title="International data transfers">
              <Typography sx={bodySx}>
                Ouro may transfer your personal data to countries outside the European Economic Area (EEA) in
                connection with the operation of the service.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                Any such transfers comply with applicable data protection laws — typically through the use of
                Standard Contractual Clauses approved by the European Commission or equivalent safeguards — to
                ensure your data receives an adequate level of protection regardless of where it is processed.
              </Typography>
            </DocSection>

            {/* Section 8 */}
            <DocSection id="g8" num="08" title="Automated decision-making">
              <Typography sx={bodySx}>
                Ouro does not engage in automated decision-making — including profiling — that produces legal
                or similarly significant effects on you without your explicit consent.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                Certain platform features (such as matchmaking ranking) involve algorithmic processing; however,
                these do not constitute automated decisions with legally binding consequences under the GDPR.
              </Typography>
            </DocSection>

            {/* Section 9 */}
            <DocSection id="g9" num="09" title="Complaints">
              <Typography sx={bodySx}>
                If you believe your data protection rights have been violated, you have the right to lodge a
                complaint with a supervisory authority in your EU member state.
              </Typography>
              <Typography sx={bodySx}>
                We encourage you to contact us first — we aim to resolve all data-related concerns directly and
                promptly.
              </Typography>
              <Callout>
                <Typography component="p">
                  EU users may also raise a dispute through the EU Online Dispute Resolution platform at{" "}
                  <strong>ec.europa.eu/odr</strong>.
                </Typography>
              </Callout>
            </DocSection>

            {/* Section 10 */}
            <DocSection id="g10" num="10" title="Changes to this policy">
              <Typography sx={bodySx}>
                We may update this GDPR and Data Protection page from time to time to reflect changes in our
                practices or legal obligations. We will notify you of any significant changes by posting the
                updated policy here and revising the "Last Updated" date.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                For material changes that affect your rights, we will send a notification to the email address
                associated with your account. Continued use of Ouro after updated terms take effect constitutes
                your acknowledgement of the revised policy.
              </Typography>
            </DocSection>

            {/* Section 11 */}
            <DocSection id="g11" num="11" title="Contact us">
              <Typography sx={bodySx}>
                To exercise your data rights, ask questions about this policy, or raise a concern, reach out
                directly. We do not route data protection inquiries through a chatbot.
              </Typography>
              <Box
                sx={{
                  mt: 2.5,
                  background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
                  border: `1px solid ${colors.border.subtle}`,
                  p: "24px 28px",
                }}
              >
                {[
                  {
                    label: "Email",
                    icon: <MailOutlineRounded sx={{ fontSize: 14 }} />,
                    val: (
                      <Box
                        component="a"
                        href={`mailto:${SUPPORT_EMAIL}`}
                        sx={{ color: colors.primary.main, textDecoration: "none" }}
                      >
                        {SUPPORT_EMAIL}
                      </Box>
                    ),
                  },
                  {
                    label: "Community",
                    icon: <DiscordIcon />,
                    val: (
                      <Box
                        component="a"
                        href={DISCORD_INVITE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: colors.primary.main, textDecoration: "none" }}
                      >
                        Discord server
                      </Box>
                    ),
                  },
                ].map((row, index, arr) => (
                  <Box
                    key={row.label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 1.75,
                      borderBottom: index < arr.length - 1 ? `1px solid ${colors.border.subtle}` : "none",
                    }}
                  >
                    <Box sx={contactIconSx}>{row.icon}</Box>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: "'Inter', system-ui, sans-serif",
                          fontSize: "10px",
                          fontWeight: 700,
                          letterSpacing: "0.15em",
                          textTransform: "uppercase",
                          color: colors.text.muted,
                          mb: 0.25,
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography sx={{ fontSize: "13px", color: colors.text.secondary }}>
                        {row.val}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </DocSection>
          </Box>
        </Box>
      </PageContainer>
    </Box>
  );
}